import { getSharedRootStore } from '../root-store';

const ROUTE_COLORS_STORAGE_PREFIX = 'monitoring-route-colors';
const DEFAULT_DAYS_TO_KEEP_ROUTE_COLORS = 4;
const MAX_DAYS_TO_KEEP_ROUTE_COLORS = 10;
const HOURS_IN_DAY = 24;
const MINUTES_IN_HOUR = 60;
const SECONDS_IN_MINUTE = 60;
const MS_IN_SECOND = 1000;
const DAYS_TO_KEEP_ROUTE_COLORS_CONFIGURATION_KEY = 'days_to_keep_route_colors';

type RouteColorsStorage = {
	colors: Record<string, RouteColorEntry>;
};

type RouteColorEntry = {
	color: string;
	expiresAt: number;
};

const clearTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

type RouteColorListener = (routeId: number, color: string) => void;

const listeners = new Set<RouteColorListener>();

const subscribe = (listener: RouteColorListener, signal?: AbortSignal): (() => void) => {
	if (signal?.aborted) {
		return () => undefined;
	}

	listeners.add(listener);
	const unsubscribe = () => listeners.delete(listener);
	signal?.addEventListener('abort', unsubscribe, { once: true });

	return unsubscribe;
};

const notify = (routeId: number, color: string) => {
	listeners.forEach(listener => listener(routeId, color));
};

const getStorageKey = (): string => {
	const {
		data: { usersStore }
	} = getSharedRootStore();
	const currentUser = usersStore.currentUser;
	const merchantId = currentUser.merchant_id;
	const userId = currentUser.id;

	return `${ROUTE_COLORS_STORAGE_PREFIX}-${merchantId}-${userId}`;
};

const getDaysToKeepRouteColors = (): number => {
	const daysToKeepRouteColors = getSharedRootStore().data.merchantConfigurationsStore.configuration
		?.dashboard_ui_configuration?.[DAYS_TO_KEEP_ROUTE_COLORS_CONFIGURATION_KEY] as number | undefined;

	return typeof daysToKeepRouteColors === 'number' &&
		daysToKeepRouteColors > 0 &&
		daysToKeepRouteColors < MAX_DAYS_TO_KEEP_ROUTE_COLORS
		? daysToKeepRouteColors
		: DEFAULT_DAYS_TO_KEEP_ROUTE_COLORS;
};

const convertDaysToMs = (): number =>
	getDaysToKeepRouteColors() * HOURS_IN_DAY * MINUTES_IN_HOUR * SECONDS_IN_MINUTE * MS_IN_SECOND;

const removeRouteColor = (storageKey: string, routeId: string) => {
	const storage = readStorageByKey(storageKey);

	if (!storage) {
		return;
	}

	delete storage.colors[routeId];

	if (Object.keys(storage.colors).length) {
		localStorage.setItem(storageKey, JSON.stringify(storage));
	} else {
		localStorage.removeItem(storageKey);
	}
};

const scheduleClear = (storageKey: string, routeId: string, expiresAt: number) => {
	const timeoutKey = `${storageKey}-${routeId}`;

	if (clearTimeouts.has(timeoutKey)) {
		clearTimeout(clearTimeouts.get(timeoutKey));
	}

	const timeoutMs = expiresAt - Date.now();
	if (timeoutMs <= 0) {
		removeRouteColor(storageKey, routeId);
		return;
	}

	clearTimeouts.set(
		timeoutKey,
		setTimeout(() => {
			removeRouteColor(storageKey, routeId);
			clearTimeouts.delete(timeoutKey);
		}, timeoutMs)
	);
};

const readStorageByKey = (storageKey: string): RouteColorsStorage | undefined => {
	const rawStorage = JSON.parse(localStorage.getItem(storageKey) || '{}') as Partial<RouteColorsStorage>;
	const storage: RouteColorsStorage = { colors: {} };

	Object.entries(rawStorage.colors || {}).forEach(([routeId, entry]) => {
		const routeColorEntry = entry as Partial<RouteColorEntry>;

		if (!routeColorEntry.color || !routeColorEntry.expiresAt || routeColorEntry.expiresAt <= Date.now()) {
			return;
		}

		storage.colors[routeId] = {
			color: routeColorEntry.color,
			expiresAt: routeColorEntry.expiresAt
		};
		scheduleClear(storageKey, routeId, routeColorEntry.expiresAt);
	});

	return storage;
};

const readStorage = (): { storageKey: string; storage: RouteColorsStorage } | undefined => {
	const storageKey = getStorageKey();

	try {
		const storage = readStorageByKey(storageKey);

		if (!storage || !Object.keys(storage.colors).length) {
			localStorage.removeItem(storageKey);
			return { storageKey, storage: { colors: {} } };
		}

		localStorage.setItem(storageKey, JSON.stringify(storage));

		return { storageKey, storage };
	} catch {
		localStorage.removeItem(storageKey);
		return { storageKey, storage: { colors: {} } };
	}
};

const saveRouteColor = (
	routeId: number,
	color: string,
	{ shouldNotify = true }: { shouldNotify?: boolean } = {}
): string => {
	const storageData = readStorage();

	if (!storageData) {
		return color;
	}

	const expiresAt = Date.now() + convertDaysToMs();
	const nextStorage: RouteColorsStorage = {
		colors: {
			...storageData.storage.colors,
			[routeId]: {
				color,
				expiresAt
			}
		}
	};

	localStorage.setItem(storageData.storageKey, JSON.stringify(nextStorage));
	scheduleClear(storageData.storageKey, String(routeId), expiresAt);

	if (shouldNotify) {
		notify(routeId, color);
	}

	return color;
};

const getRouteColor = (routeId: number): string | undefined => {
	return readStorage()?.storage.colors[routeId]?.color;
};

const getOrSaveRouteColor = (routeId: number, getInitialColor: () => string): string => {
	const savedColor = getRouteColor(routeId);

	if (savedColor) {
		return savedColor;
	}

	return saveRouteColor(routeId, getInitialColor(), { shouldNotify: false });
};

export const routeColorsStorage = {
	getRouteColor,
	getOrSaveRouteColor,
	saveRouteColor,
	subscribe
};
